import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Users as UsersIcon, UserPlus, Edit, Trash2, Shield, Camera, Phone, Mail, MapPin, CreditCard, Factory } from 'lucide-react';
import { SimpleSelect } from '../components/ui/simple-select';
import { toast } from 'sonner';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';
import { listaCompletaPapeis } from '../lib/papeisNotificacao';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

// Cores para cada perfil
const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800',
  engenheiro: 'bg-blue-100 text-blue-800',
  gerente_producao: 'bg-purple-100 text-purple-800',
  producao: 'bg-green-100 text-green-800'
};

const getRoleColor = (role) => {
  return ROLE_COLORS[role] || 'bg-gray-100 text-gray-800';
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    telefone_whatsapp: '',
    cpf: '',
    birth_date: '',
    address: '',
    bank_name: '',
    bank_agency: '',
    bank_account: '',
    profile_photo: '',
    role: '',
    acesso_todas_fabricas: true,
    fabricas_ids: [],
    limite_aprovacao_brl: 0,
    papeis_notificacao: [],
  });
  const [papeisCatalogo, setPapeisCatalogo] = useState([]);

  useEffect(() => {
    document.title = "Gestão de Usuários — NautilusOS";
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, profilesRes, factoriesRes, papeisRes] = await Promise.all([
        api.get('/users'),
        api.get('/profiles'),
        api.get('/factories'),
        api.get('/users/papeis-disponiveis')
      ]);
      setUsers(usersRes.data);
      setProfiles(profilesRes.data);
      setFactories(factoriesRes.data || []);
      setPapeisCatalogo(listaCompletaPapeis(papeisRes.data || []));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    }
  };

  // Converte perfis para formato do SimpleSelect
  const profileOptions = profiles.map(p => ({
    value: p.slug,
    label: p.name
  }));

  // Encontra o nome do perfil pelo slug
  const getProfileName = (slug) => {
    const profile = profiles.find(p => p.slug === slug);
    return profile?.name || slug;
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      password: '',
      phone: '',
      telefone_whatsapp: '',
      cpf: '',
      birth_date: '',
      address: '',
      bank_name: '',
      bank_agency: '',
      bank_account: '',
      profile_photo: '',
      role: '',
      acesso_todas_fabricas: true,
      fabricas_ids: [],
      limite_aprovacao_brl: 0,
      papeis_notificacao: [],
    });
    setActiveTab('basic');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!formData.full_name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (!editingUser && !formData.email.trim()) {
      toast.error('Email é obrigatório');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Telefone é obrigatório');
      return;
    }
    if (!formData.cpf.trim()) {
      toast.error('CPF é obrigatório');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('Senha é obrigatória');
      return;
    }
    if (!formData.role) {
      toast.error('Perfil é obrigatório');
      return;
    }
    
    try {
      if (editingUser) {
        const updateData = {
          full_name: formData.full_name,
          phone: formData.phone,
          telefone_whatsapp: formData.telefone_whatsapp,
          cpf: formData.cpf,
          birth_date: formData.birth_date,
          address: formData.address,
          bank_name: formData.bank_name,
          bank_agency: formData.bank_agency,
          bank_account: formData.bank_account,
          profile_photo: formData.profile_photo,
          role: formData.role,
          is_active: true,
          acesso_todas_fabricas: formData.acesso_todas_fabricas,
          fabricas_ids: formData.acesso_todas_fabricas ? [] : formData.fabricas_ids,
          limite_aprovacao_brl: parseFloat(formData.limite_aprovacao_brl) || 0,
          papeis_notificacao: formData.papeis_notificacao,
        };
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('Usuário atualizado!');
      } else {
        const createData = {
          ...formData,
          fabricas_ids: formData.acesso_todas_fabricas ? [] : formData.fabricas_ids
        };
        await api.post('/users', createData);
        toast.success('Usuário criado!');
      }
      
      setShowDialog(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Erro ao salvar';
      toast.error(message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      telefone_whatsapp: user.telefone_whatsapp || '',
      cpf: user.cpf || '',
      birth_date: user.birth_date || '',
      address: user.address || '',
      bank_name: user.bank_name || '',
      bank_agency: user.bank_agency || '',
      bank_account: user.bank_account || '',
      profile_photo: user.profile_photo || '',
      role: user.role || '',
      acesso_todas_fabricas: user.acesso_todas_fabricas !== false,
      fabricas_ids: user.fabricas_ids || [],
      limite_aprovacao_brl: user.limite_aprovacao_brl || 0,
      papeis_notificacao: user.papeis_notificacao || [],
    });
    setActiveTab('basic');
    setShowDialog(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleteUserId}`);
      toast.success('Usuário excluído!');
      setDeleteUserId(null);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Imagem deve ter no máximo 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, profile_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  if (loading) {
    return <div className="flex justify-center py-8">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <TitleWithHelp
            title="Gestão de Usuários"
            description="Cadastro de colaboradores com dados pessoais, bancários e foto. O perfil de acesso define as permissões. O acesso às fábricas controla quais dados o usuário pode visualizar."
            connections={['Perfis de Acesso', 'Fábricas']}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <UsersIcon className="h-6 w-6 text-blue-600" />
              Gestão de Usuários
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Gerencie acessos da equipe</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingUser(null); setShowDialog(true); }}>
          <UserPlus className="mr-2" size={20} />
          Novo Usuário
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map(user => (
          <Card key={user.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  {user.profile_photo ? (
                    <img 
                      src={user.profile_photo} 
                      alt={user.full_name}
                      className="w-10 h-10 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="bg-blue-600 p-2 rounded-full shrink-0">
                      <UsersIcon size={16} className="text-white" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <CardTitle className="text-base truncate">{user.full_name}</CardTitle>
                    <p className="text-sm text-gray-600 truncate">{user.email}</p>
                  </div>
                </div>
                <Badge className={`${getRoleColor(user.role)} shrink-0 whitespace-nowrap`}>
                  {getProfileName(user.role)}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="space-y-1 text-sm text-gray-600 mb-4">
                {user.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={14} />
                    <span>{user.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Shield size={14} />
                  <span>{user.is_active ? 'Ativo' : 'Inativo'}</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(user)} className="flex-1">
                  <Edit className="h-3 w-3 mr-1" />
                  Editar
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setDeleteUserId(user.id)}
                  className="flex-1 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Excluir
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog Criar/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Dados Básicos</TabsTrigger>
                <TabsTrigger value="personal">Dados Pessoais</TabsTrigger>
                <TabsTrigger value="bank">Dados Bancários</TabsTrigger>
                <TabsTrigger value="access">Acesso Fábricas</TabsTrigger>
              </TabsList>
              
              {/* Tab Dados Básicos */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                {/* Foto de Perfil */}
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    {formData.profile_photo ? (
                      <img 
                        src={formData.profile_photo} 
                        alt="Preview"
                        className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                        <UsersIcon size={32} className="text-gray-400" />
                      </div>
                    )}
                    <label
                      htmlFor="user-photo-upload"
                      className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 cursor-pointer"
                    >
                      <Camera size={16} />
                    </label>
                    <input
                      ref={fileInputRef}
                      id="user-photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="sr-only"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome Completo *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      placeholder="Nome completo"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      disabled={!!editingUser}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Telefone *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: formatPhone(e.target.value)})}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                  </div>
                  <div>
                    <Label>CPF *</Label>
                    <Input
                      value={formData.cpf}
                      onChange={(e) => setFormData({...formData, cpf: formatCPF(e.target.value)})}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                  </div>
                </div>

                <div>
                  <Label>WhatsApp (Z-API - notificações)</Label>
                  <Input
                    data-testid="user-telefone-whatsapp"
                    value={formData.telefone_whatsapp}
                    onChange={(e) => setFormData({...formData, telefone_whatsapp: formatPhone(e.target.value)})}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Usado para envio de alertas do sistema via WhatsApp. Deixe em branco para não receber.
                  </p>
                </div>
                
                {!editingUser && (
                  <div>
                    <Label>Senha *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Senha de acesso"
                    />
                  </div>
                )}
                
                <div>
                  <Label>Perfil *</Label>
                  {profileOptions.length > 0 ? (
                    <SimpleSelect
                      options={profileOptions}
                      value={formData.role}
                      onChange={(value) => setFormData({...formData, role: value})}
                      placeholder="Selecione um perfil"
                    />
                  ) : (
                    <p className="text-sm text-gray-500 mt-2">Carregando perfis...</p>
                  )}
                </div>

                <div>
                  <Label>Limite de Aprovação de Cotações (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.limite_aprovacao_brl}
                    onChange={(e) => setFormData({...formData, limite_aprovacao_brl: e.target.value})}
                    placeholder="0,00"
                    data-testid="input-limite-aprovacao"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Valor máximo que este usuário pode aprovar (se tiver a feature <code>compras_aprovar</code>).
                    Deixe 0 para "sem limite".
                  </p>
                </div>

                <div>
                  <Label>Papéis de Notificação</Label>
                  <p className="text-xs text-gray-500 mb-1.5">
                    Marque os papéis deste usuário. Quem estiver marcado como "Financeiro", por exemplo,
                    já recebe automaticamente os alertas de Pedido Aprovado, Orçamento Estourado e Margem Crítica.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {papeisCatalogo.map((p) => (
                      <label
                        key={p.valor}
                        className="flex items-center gap-2 text-sm cursor-pointer px-3 py-2 rounded border border-gray-200 hover:border-indigo-300"
                        data-testid={`user-papel-checkbox-${p.valor}`}
                      >
                        <Checkbox
                          checked={formData.papeis_notificacao.includes(p.valor)}
                          onCheckedChange={(checked) => setFormData({
                            ...formData,
                            papeis_notificacao: checked
                              ? [...formData.papeis_notificacao, p.valor]
                              : formData.papeis_notificacao.filter((x) => x !== p.valor),
                          })}
                        />
                        {p.label}
                      </label>
                    ))}
                  </div>
                </div>
              </TabsContent>
              
              {/* Tab Dados Pessoais */}
              <TabsContent value="personal" className="space-y-4 mt-4">
                <div>
                  <Label>Data de Nascimento</Label>
                  <Input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Endereço Completo</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Rua, número, bairro, cidade, estado"
                  />
                </div>
              </TabsContent>
              
              {/* Tab Dados Bancários */}
              <TabsContent value="bank" className="space-y-4 mt-4">
                <p className="text-sm text-gray-500 mb-4">
                  <CreditCard className="inline mr-2" size={16} />
                  Dados para pagamento (opcional)
                </p>
                
                <div>
                  <Label>Banco</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    placeholder="Ex: Banco do Brasil, Itaú, Nubank..."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Agência</Label>
                    <Input
                      value={formData.bank_agency}
                      onChange={(e) => setFormData({...formData, bank_agency: e.target.value})}
                      placeholder="0000"
                    />
                  </div>
                  <div>
                    <Label>Conta</Label>
                    <Input
                      value={formData.bank_account}
                      onChange={(e) => setFormData({...formData, bank_account: e.target.value})}
                      placeholder="00000-0"
                    />
                  </div>
                </div>
              </TabsContent>
              
              {/* Tab Acesso às Fábricas */}
              <TabsContent value="access" className="space-y-4 mt-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                  <div className="flex items-center gap-2 text-blue-700">
                    <Factory className="h-5 w-5" />
                    <span className="font-medium">Controle de Acesso às Fábricas</span>
                    <HelpTooltip 
                      title="Acesso às Fábricas"
                      description="Esta configuração limita quais dados o usuário pode visualizar. Se restrito, o usuário só verá projetos, cronogramas e fases das fábricas selecionadas."
                      connections={['Fábricas', 'Produção', 'Cronograma']}
                      iconSize={14}
                    />
                  </div>
                  <p className="text-sm text-blue-600 mt-1">
                    Define quais fábricas o usuário pode visualizar na tela de Produção
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      formData.acesso_todas_fabricas 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setFormData({...formData, acesso_todas_fabricas: true, fabricas_ids: []})}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.acesso_todas_fabricas ? 'border-green-500 bg-green-500' : 'border-gray-300'
                      }`}>
                        {formData.acesso_todas_fabricas && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">Acesso a todas as fábricas</p>
                        <p className="text-sm text-gray-500">O usuário pode ver projetos e fases de todas as fábricas</p>
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      !formData.acesso_todas_fabricas 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setFormData({...formData, acesso_todas_fabricas: false})}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        !formData.acesso_todas_fabricas ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                      }`}>
                        {!formData.acesso_todas_fabricas && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">Acesso apenas a fábricas específicas</p>
                        <p className="text-sm text-gray-500">Selecione quais fábricas o usuário pode acessar</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Lista de fábricas para seleção */}
                  {!formData.acesso_todas_fabricas && (
                    <div className="border rounded-lg p-4 mt-4">
                      <Label className="mb-3 block">Selecione as fábricas:</Label>
                      {factories.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          Nenhuma fábrica cadastrada. Cadastre fábricas primeiro.
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {factories.map(factory => (
                            <div 
                              key={factory.id}
                              className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                formData.fabricas_ids.includes(factory.id) ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => {
                                const newIds = formData.fabricas_ids.includes(factory.id)
                                  ? formData.fabricas_ids.filter(id => id !== factory.id)
                                  : [...formData.fabricas_ids, factory.id];
                                setFormData({...formData, fabricas_ids: newIds});
                              }}
                            >
                              <Checkbox 
                                checked={formData.fabricas_ids.includes(factory.id)}
                                className="pointer-events-none"
                              />
                              <div>
                                <p className="font-medium">{factory.name}</p>
                                <p className="text-xs text-gray-500">{factory.location}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingUser ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Exclusão */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja excluir este usuário? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
